// SHARED CODE WITH FRONTEND
import { fieldRegex } from '$/shared/frontend/utils/urls/URLUtils';
import { emailFields } from '$/sections/Home/WebinarEditor/NotificationsEditor/EmailFields';

declare global {
  interface String {
    notEmpty(): boolean;
    capitalize(): string;
    capitalizeFirstLetter(): string;
  }
}

export const notEmpty = (str: string | undefined | null): boolean => {
  return Boolean(str) && str!.length > 0;
};

export function pad00(n: number): string {
  let t = Math.abs(n).toString();
  if (t.length < 2) {
    t = `0${t}`;
  }
  return n < 0 ? `-${t}` : t;
}

export function pad000(n: number): string {
  let t = Math.abs(n).toString();
  if (t.length < 2) {
    t = `00${t}`;
  }
  if (t.length < 3) {
    t = `0${t}`;
  }
  return n < 0 ? `-${t}` : t;
}

String.prototype.notEmpty = function (this: string): boolean {
  return Boolean(this) && this.length > 0;
};

String.prototype.capitalize = function (this: string): string {
  if (!this || this.length === 0) {
    return this;
  }
  return this.charAt(0).toUpperCase() + this.slice(1);
};

String.prototype.capitalizeFirstLetter = function (this: string): string {
  return this.charAt(0).toLocaleUpperCase() + this.slice(1);
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const makeGlobal = 0;

const picUrlRegex = /(imgix\.|\.jpe?g|\.png)/;

export const isNotVideo = (url?: string | null): boolean => {
  if (url && picUrlRegex.exec(url)) {
    return true;
  }
  return false;
};

export const isValidUrl = (url: string): boolean => {
  const regex = new RegExp(
    '^(?:(?:(?:https?|ftp):)?//)(?:S+(?::S*)?@)?(?:(?!(?:10|127)(?:.d{1,3}){3})(?!(?:169.254|192.168)(?:.d{1,3}){2})(?!172.(?:1[6-9]|2d|3[0-1])(?:.d{1,3}){2})(?:[1-9]d?|1dd|2[01]d|22[0-3])(?:.(?:1?d{1,2}|2[0-4]d|25[0-5])){2}(?:.(?:[1-9]d?|1dd|2[0-4]d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:.(?:[a-z\u00a1-\uffff]{2,})))(?::d{2,5})?(?:[/?#]S*)?$'
  );
  if (url.match(regex)) {
    return true;
  }
  return false;
};

export const isHtmlFormatting = (value: string): boolean => {
  const regex = new RegExp('(</([^>]+)>)|&nbsp;');
  if (value.match(regex)) {
    return true;
  }
  return false;
};

export const hash = (value?: string): number => {
  if (!value || value.toLowerCase() === 'standard') {
    return 0;
  }

  let hash = 0,
    i,
    chr;
  for (i = 0; i < value.length; i++) {
    chr = value.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash + value.length);
};

export const doesExist = (value?: any): boolean => {
  if (!value || value === undefined || value === '' || !Object.keys(value).length) return false;
  return true;
};

export const stringToMarkdown = (value: string): string => {
  return value
    .replace(/([^\n]+)([\n])/g, (_match: string, p1: string, _p2: string) => `${p1}#nl##nl#`)
    .replace(/\n/g, '\u200b#nl##nl#')
    .replace(/#nl#/g, '\n');
};

export const isBase64 = (value: string): boolean => {
  if (!value) return false;
  if (value.startsWith('data:')) {
    return true;
  } else {
    return false;
  }
};

export const maxCharsMessage = (maxChars: number): string => {
  return `Please limit to ${maxChars} characters or less.`;
};

const standardizedField = (value: string): string => {
  return value.replace(/[{}\s]/g, '');
};

export const countCharsInMarkdown = (value: string): number => {
  const fieldsUsed = value.match(fieldRegex)?.map((f: string) => standardizedField(f));
  let fieldsLength = 0;
  // count chars when using mention fields
  if (fieldsUsed) {
    fieldsUsed.map((field) => {
      const fieldExist = emailFields.find((f) => field === standardizedField(f.name));
      if (fieldExist) {
        fieldsLength = fieldsLength + fieldExist.maxLength;
      }
    });
  }
  // count chars from markdown string
  const textLength = value
    .replace(/{[^}]+}/g, '')
    .replace(/\n\n\u200b/g, '\n')
    .replace(/\n\n/g, '\n').length;

  return fieldsLength + textLength;
};

export const isValidMaxCharsMarkdown = (maxChars: number, value?: string): boolean => {
  if (!value) return true;
  const hasEndCharacter = value.charAt(value.length - 1) === '\n' ? true : false;
  return countCharsInMarkdown(value) <= (hasEndCharacter ? maxChars + 1 : maxChars);
};

export const getMaxCharsMessage = (maxChars?: number, value?: string): string | null => {
  return maxChars && !isValidMaxCharsMarkdown(maxChars, value) ? maxCharsMessage(maxChars) : null;
};

/**
 * Do not allow empty string for propertyName
 * @param value
 * @returns
 */
export const normalizePropertyName = (value?: string | null): string | null | undefined => {
  if (value === undefined || value === null) return value;
  value = value.trim();
  return value === '' ? null : value;
};

/**
 * Convert a string to propertyName
 * @param value
 * @returns
 */
export const convertStringtoPropertyName = (value?: string | null): string | null | undefined => {
  if (value === undefined || value === null) return value;
  return value
    .replace(/[^\w\s]/g, '')
    .split(' ')
    .map((word, index) => {
      if (index === 0) return word.toLowerCase();
      return capitalizeFirstLetter(word);
    })
    .join('');
};

/**
 * Generate unique propertyName to prevent conflicts with the existing properties
 * @param propertyName
 * @param excludeProperties
 * @returns
 */
export const generateUniquePropertyName = (
  propertyName: string,
  excludeProperties: string[]
): string => {
  const excludedPropertiesSet = new Set(excludeProperties);
  let i = 1;
  if (excludedPropertiesSet.has(propertyName.toLowerCase())) {
    propertyName = propertyName + `_${i++}`;
    while (excludedPropertiesSet.has(propertyName.toLowerCase())) {
      const splitNames: string[] = propertyName.split('_');
      splitNames[splitNames.length - 1] = i.toString();
      propertyName = splitNames.join('_');
      i++;
    }
  }
  return propertyName;
};

export const removeZeroWidthChar = (str: string): string => {
  return str.replace(/[\u200B-\u200D\uFEFF]/g, '');
};

/**
 * Create a safe regex pattern from a string
 * @param str
 * @returns RegExp
 */
export const stringToRegex = (str: string): RegExp => {
  return new RegExp(
    str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), // Escape special characters
    'i' // Case-insensitive flag
  );
};

// @see: https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
export const hash53 = (str: string, seed = 0): number => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export const hashChatMessageId = (conversationId: string, type: string): string => {
  let id = `${conversationId}:${type}`;
  try {
    id = hash53(id).toString();
  } catch (error) {
    console.error('hashChatMessageId failed', error);
  }
  // adding conversionId in case hash53 has a collision
  return btoa(`${conversationId}:${id}`);
};

export const extractDomainFromEmail = (email: string): string | null => {
  // Known public email domains
  const publicDomains = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'icloud.com',
    'aol.com',
    'yandex.com',
    'mail.com',
    'zoho.com',
  ];

  // Extract the domain from the email
  const domain = email.split('@')[1];

  // Check if the domain is in the list of public email providers
  if (publicDomains.includes(domain)) return null;

  return domain;
};

export const extractDashboardFilterString = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.delete('aid');
  url.searchParams.delete('id');
  url.searchParams.delete('setId');
  const searchParams = url.search;
  return searchParams.replace(/^\?/, '');
};
